// src/app/root.tsx
import {
    isRouteErrorResponse,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
} from "react-router";
import { ClerkProvider } from "@clerk/react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { deDE } from "@clerk/localizations";

import type { Route } from "../.react-router/types/app/+types/root";
import "./tailwind.css";

// Export for other files that need loader data shape
export interface RootLoaderData {
    isAdmin: boolean;
    publicEnv: {
        VITE_SENTRY_DSN?: string;
        VITE_POSTHOG_API_KEY?: string;
    };
}

export const middleware = [clerkMiddleware()];

export const loader = (args: Route.LoaderArgs) => {
    return rootAuthLoader(args, async ({ request }) => {
        const { userId } = request.auth;
        const isAdmin = userId ? true : false;

        return {
            isAdmin,
            publicEnv: {
                VITE_SENTRY_DSN: process.env.VITE_SENTRY_DSN,
                VITE_POSTHOG_API_KEY: process.env.VITE_POSTHOG_API_KEY,
            },
        };
    });
};

export const links: Route.LinksFunction = () => [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
    },
];

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="de">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

const clerkAppearance = {
    baseTheme: undefined,
    elements: {
        footer: "hidden",
        rootBox: {
            boxShadow: "none",
        },
    },
};

export default function App({ loaderData }: Route.ComponentProps) {
    const publicEnv = loaderData.publicEnv ?? {};
    const publicEnvScript = `window.__ENV = ${JSON.stringify(publicEnv).replace(/</g, "\\u003c")};`;

    return (
        <>
            <script dangerouslySetInnerHTML={{ __html: publicEnvScript }} />
            <ClerkProvider
                loaderData={loaderData}
                appearance={clerkAppearance}
                localization={deDE}
            >
                <Outlet />
            </ClerkProvider>
        </>
    );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    console.error("ErrorBoundary", error);
    captureRemixErrorBoundaryError(error);

    let message = "Etwas ist schiefgegangen";
    let details = "Wir wurden benachrichtigt und kümmern uns darum.";
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? "404 - Nicht gefunden" : `Fehler ${error.status}`;
        details =
            error.status === 404
                ? "Die angeforderte Seite konnte nicht gefunden werden."
                : error.statusText || details;
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message;
        stack = error.stack;
    }

    return (
        <div className="flex min-h-screen items-center justify-center">
            <div className="text-center max-w-md px-4">
                <h1 className="text-2xl font-semibold">{message}</h1>
                <p className="mt-2 text-gray-600">{details}</p>
                {stack && (
                    <pre className="mt-4 w-full p-4 overflow-x-auto text-left text-sm bg-gray-100 rounded">
                        <code>{stack}</code>
                    </pre>
                )}
            </div>
        </div>
    );
}
